/**
 * «Жалобы»: без ответа руководителя салона, салоны и потери, все жалобы за
 * период. Спека `docs/specs/2026-09-25-complaints-design.md`; числа — те же,
 * что у `prokat-runner complaints-report` / `complaints-unanswered`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  ANSWER_LABEL,
  type Complaint,
  type ComplaintsReport,
  type Count,
  crmComplaintUrl,
  loadComplaints,
  presets,
  rub,
  shiftPeriod,
} from "@/lib/complaints";
import { NavMenu } from "@/nav/NavMenu";

type Tab = "unanswered" | "salons" | "list";
const TABS: { id: Tab; label: string }[] = [
  { id: "unanswered", label: "Без ответа руководителя" },
  { id: "salons", label: "Салоны и потери" },
  { id: "list", label: "Все жалобы" },
];

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const input =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const btn =
  "h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg-muted hover:bg-bg-hover";
const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(2, 4)}`;

function Delta({
  now,
  prev,
  money,
}: {
  now: number;
  prev: number;
  money?: boolean;
}) {
  const d = now - prev;
  if (!d) return <span className="text-fg-faint">как в прошлом периоде</span>;
  // Больше жалоб и потерь — хуже.
  return (
    <span className={d > 0 ? "text-err" : "text-ok"}>
      {d > 0 ? "+" : "−"}
      {money ? rub(Math.abs(d)) : Math.abs(d)} к прошлому (
      {money ? rub(prev) : prev})
    </span>
  );
}

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-1 p-4 ${card}`}
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function Tiles({ r }: { r: ComplaintsReport }) {
  const t = r.total;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
      <Tile
        label="Жалоб"
        value={t.count}
        hint={<Delta now={t.count} prev={r.prev_total.count} />}
      />
      <Tile
        label="Без ответа руководителя"
        value={
          <span className={t.unanswered ? "text-err" : ""}>{t.unanswered}</span>
        }
        hint={
          t.no_head
            ? `ещё ${t.no_head} — салон без руководителя или без салона`
            : undefined
        }
      />
      <Tile
        label="Штрафы сотрудникам"
        value={rub(t.penalty)}
        hint={<Delta now={t.penalty} prev={r.prev_total.penalty} money />}
      />
      <Tile
        label="Расход салонов"
        value={rub(t.expense)}
        hint="sHelp_task.expenseSum"
      />
      <Tile
        label="С внешних площадок"
        value={t.external}
        hint="Яндекс, Google, 2gis, Авито"
      />
    </div>
  );
}

function AnswerBadge({ c }: { c: Complaint }) {
  const s = c.answer.state;
  const cls =
    s === "answered"
      ? "text-ok"
      : s === "unanswered"
        ? "text-err"
        : "text-warn";
  return (
    <span
      className={cls}
      title={c.answer.at ? `${c.answer.by}, ${c.answer.at}` : undefined}
    >
      {s === "answered" ? `ответила: ${c.answer.by}` : ANSWER_LABEL[s]}
    </span>
  );
}

function Refs({ c }: { c: Complaint }) {
  return (
    <span className="inline-flex items-center gap-2 text-[12px]">
      <a
        href={crmComplaintUrl(c.id)}
        target="_blank"
        rel="noreferrer"
        className="font-mono text-accent hover:underline"
      >
        #{c.id}
      </a>
      {c.deal_id && (
        <>
          <Link
            to={`/deals/${c.deal_id}`}
            className="font-mono text-accent hover:underline"
          >
            сделка {c.deal_id}
          </Link>
          <NavMenu r={{ obj: "dealID", id: c.deal_id }} />
        </>
      )}
    </span>
  );
}

function Comments({ c }: { c: Complaint }) {
  if (!c.comments.length)
    return (
      <div className="text-[12.5px] text-fg-faint">
        Комментариев нет — даже текста жалобы.
      </div>
    );
  return (
    <ol className="flex flex-col gap-2">
      {c.comments.map((m, i) => (
        <li
          key={m.id}
          className={`rounded-[var(--radius-ui)] border px-3 py-2 ${m.is_head ? "border-ok/50" : "border-border"}`}
        >
          <div className="mb-1 text-[11.5px] text-fg-faint">
            {i === 0 ? "текст жалобы · " : m.is_head ? "руководитель · " : ""}
            {m.user ?? m.user_id} · {m.at.slice(0, 16)}
          </div>
          <div className="whitespace-pre-wrap text-[12.5px]">{m.text}</div>
        </li>
      ))}
    </ol>
  );
}

function Money({ c }: { c: Complaint }) {
  if (!c.penalty && !c.expense) return null;
  return (
    <div className="text-[12.5px]">
      {c.penalty > 0 && (
        <div>
          Штрафы {rub(c.penalty)}:{" "}
          {c.penalties
            .map(
              (p) =>
                `${p.name ?? p.user_id}${p.location ? ` (${p.location})` : ""} ${rub(p.amount)}`,
            )
            .join(", ")}
        </div>
      )}
      {c.expense > 0 && <div>Расход салона {rub(c.expense)}</div>}
    </div>
  );
}

function Unanswered({ r }: { r: ComplaintsReport }) {
  const items = r.items.filter(
    (i) => i.in_stat && i.answer.state !== "answered",
  );
  if (!items.length)
    return (
      <div className={`p-6 text-[13px] text-fg-muted ${card}`}>
        На все жалобы периода руководители ответили.
      </div>
    );
  return (
    <div className="flex flex-col gap-3">
      {items.map((c) => {
        const others = c.comments.slice(1).map((m) => m.user);
        return (
          <section
            key={c.id}
            className={`flex flex-col gap-2 p-4 ${card}`}
            data-annot={`жалоба без ответа ${c.id}`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
              <Refs c={c} />
              <b>{c.location_name ?? "без салона"}</b>
              <span className="text-fg-muted">{dm(c.date)}</span>
              <span className={c.external ? "text-err" : "text-fg-muted"}>
                {c.category}
              </span>
              <span className="text-fg-muted">{c.status}</span>
              {c.groups.length > 0 && (
                <span className="text-fg-muted">{c.groups.join(", ")}</span>
              )}
              <span className="ml-auto">
                <AnswerBadge c={c} />
                {c.waiting_days !== null && (
                  <span className="ml-2 text-fg-faint">
                    ждёт {c.waiting_days} дн.
                  </span>
                )}
              </span>
            </div>
            <div className="text-[12px] text-fg-muted">
              Руководитель:{" "}
              {c.heads.length ? c.heads.join(", ") : "не назначен в CRM"}
              {others.length > 0 && (
                <> · отвечали (не руководитель): {others.join(", ")}</>
              )}
            </div>
            <div className="whitespace-pre-wrap text-[13px]">
              {c.text ?? <span className="text-fg-faint">текста нет</span>}
            </div>
            <Money c={c} />
          </section>
        );
      })}
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: Count[] }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <section className={`p-4 ${card}`} data-annot={`разрез «${title}»`}>
      <h3 className="mb-2 text-[13px] font-medium">{title}</h3>
      {rows.length ? (
        <table className="w-full text-[12.5px]">
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)}>
                <td className="py-1 pr-2">{r.name ?? "—"}</td>
                <td className="w-24 py-1">
                  <div className="h-1.5 rounded bg-fg-faint/20">
                    <div
                      className="h-1.5 rounded bg-fg-muted"
                      style={{ width: `${(r.count / max) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="w-10 py-1 text-right tabular-nums">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-[12.5px] text-fg-faint">нет</div>
      )}
    </section>
  );
}

function Salons({ r }: { r: ComplaintsReport }) {
  const th = "whitespace-nowrap px-3 py-2 text-right font-medium";
  const td = "whitespace-nowrap px-3 py-1.5 text-right tabular-nums";
  return (
    <div className="flex flex-col gap-4">
      <section className={card} data-annot="рейтинг салонов">
        <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">
          Салоны: у кого больше жалоб и во сколько они обошлись
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-2 font-medium">Салон</th>
                <th className="px-3 py-2 font-medium">Руководитель</th>
                <th className={th}>Жалоб</th>
                <th className={th} title={`${r.prev.from} — ${r.prev.to}`}>
                  Было
                </th>
                <th className={th}>Внешние</th>
                <th className={th}>Без ответа</th>
                <th className={th}>Штрафы</th>
                <th className={th}>Было штрафов</th>
                <th className={th}>Расход</th>
              </tr>
            </thead>
            <tbody>
              {r.salons.map((s) => (
                <tr
                  key={String(s.location_id)}
                  className="border-t border-border hover:bg-bg-hover"
                  data-annot={`салон ${s.short}`}
                >
                  <td className="whitespace-nowrap px-4 py-1.5 font-medium">
                    {s.name ?? "без салона"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">
                    {s.heads.join(", ") || "—"}
                  </td>
                  <td className={`${td} font-semibold`}>{s.count}</td>
                  <td className={`${td} text-fg-muted`}>{s.prev_count}</td>
                  <td className={td}>
                    {s.external || <span className="text-fg-faint">0</span>}
                  </td>
                  <td
                    className={`${td} ${s.unanswered ? "text-err" : "text-fg-faint"}`}
                  >
                    {s.unanswered}
                  </td>
                  <td
                    className={`${td} ${s.penalty ? "font-semibold" : "text-fg-faint"}`}
                  >
                    {rub(s.penalty)}
                  </td>
                  <td className={`${td} text-fg-muted`}>
                    {rub(s.prev_penalty)}
                  </td>
                  <td className={`${td} ${s.expense ? "" : "text-fg-faint"}`}>
                    {rub(s.expense)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11.5px] text-fg-faint">
          Не считаются статусы «Не существена» и «Ошибка». Штрафы — проводки
          «Жалоба.Штраф», привязанные к жалобе; идут в салон жалобы. Прошлый
          период: {dm(r.prev.from)} — {dm(r.prev.to)}.
        </div>
      </section>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
        <Breakdown title="Категория" rows={r.by_category} />
        <Breakdown title="Группа (причина)" rows={r.by_group} />
        <Breakdown title="Статус" rows={r.by_status} />
        <Breakdown title="Кто добавил" rows={r.by_author} />
      </div>
      <section className={`p-4 ${card}`} data-annot="штрафы по сотрудникам">
        <h3 className="mb-2 text-[13px] font-medium">Штрафы по сотрудникам</h3>
        {r.penalties.length ? (
          <table className="w-full text-[12.5px]">
            <tbody>
              {r.penalties.map((p) => (
                <tr key={p.user_id} className="border-t border-border">
                  <td className="py-1.5">
                    {p.name ?? p.user_id}{" "}
                    <span className="text-fg-faint">{p.location}</span>
                  </td>
                  <td className="py-1.5 text-right text-fg-muted">
                    жалоб {p.count}
                  </td>
                  <td className="w-28 py-1.5 text-right font-semibold tabular-nums">
                    {rub(p.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-[12.5px] text-fg-faint">
            За период штрафов по жалобам нет.
          </div>
        )}
      </section>
    </div>
  );
}

function List({ r }: { r: ComplaintsReport }) {
  const [f, setF] = useState({ loc: "", cat: "", group: "", status: "" });
  const [open, setOpen] = useState<number | null>(null);
  const uniq = (xs: (string | null)[]) =>
    [...new Set(xs.filter((x): x is string => !!x))].sort();
  const opts = {
    loc: uniq(r.items.map((i) => i.location_name)),
    cat: uniq(r.items.map((i) => i.category)),
    group: uniq(r.items.flatMap((i) => i.groups)),
    status: uniq(r.items.map((i) => i.status)),
  };
  const items = r.items.filter(
    (i) =>
      (!f.loc || i.location_name === f.loc) &&
      (!f.cat || i.category === f.cat) &&
      (!f.group || i.groups.includes(f.group)) &&
      (!f.status || i.status === f.status),
  );
  const sel = (k: keyof typeof f, label: string) => (
    <select
      value={f[k]}
      onChange={(e) => setF({ ...f, [k]: e.target.value })}
      className={input}
      data-annot={`фильтр ${label}`}
    >
      <option value="">{label}: все</option>
      {opts[k].map((o) => (
        <option key={o}>{o}</option>
      ))}
    </select>
  );
  return (
    <section className={card} data-annot="все жалобы">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="mr-2 text-[13px] font-medium">Жалоб: {items.length}</h2>
        {sel("loc", "Салон")}
        {sel("cat", "Категория")}
        {sel("group", "Группа")}
        {sel("status", "Статус")}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">№</th>
              <th className="px-3 py-2 font-medium">Дата</th>
              <th className="px-3 py-2 font-medium">Салон</th>
              <th className="px-3 py-2 font-medium">Категория</th>
              <th className="px-3 py-2 font-medium">Группа</th>
              <th className="px-3 py-2 font-medium">Статус</th>
              <th className="px-3 py-2 font-medium">Ответ</th>
              <th className="px-3 py-2 text-right font-medium">Потери</th>
              <th className="px-3 py-2 font-medium">Текст</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <Fragment key={c.id}>
                <tr
                  onClick={() => setOpen(open === c.id ? null : c.id)}
                  className={`cursor-pointer border-t border-border hover:bg-bg-hover ${c.in_stat ? "" : "opacity-50"}`}
                  data-annot={`жалоба ${c.id}`}
                >
                  <td className="px-4 py-1.5 font-mono">{c.id}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {dm(c.date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {c.location ?? "—"}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-1.5 ${c.external ? "text-err" : ""}`}
                  >
                    {c.category}
                  </td>
                  <td className="px-3 py-1.5 text-fg-muted">
                    {c.groups.join(", ") || "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{c.status}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <AnswerBadge c={c} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                    {c.penalty + c.expense ? (
                      rub(c.penalty + c.expense)
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                  <td className="max-w-[280px] truncate px-3 py-1.5 text-fg-muted">
                    {c.text ?? "—"}
                  </td>
                </tr>
                {open === c.id && (
                  <tr className="bg-bg">
                    <td colSpan={9} className="px-4 py-3">
                      <div className="max-w-[1000px]">
                        <div className="mb-2 flex flex-wrap items-center gap-3 text-[12px] text-fg-muted">
                          <Refs c={c} />
                          <span>добавил(а): {c.author ?? c.author_id}</span>
                          <span>руководитель: {c.heads.join(", ") || "—"}</span>
                        </div>
                        <Money c={c} />
                        <div className="mt-2">
                          <Comments c={c} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Строку можно раскрыть. Бледные — «Не существена» / «Ошибка», в рейтинг
        не входят.
      </div>
    </section>
  );
}

export function ComplaintsPage() {
  const [params, setParams] = useSearchParams();
  const def = presets()[0]!;
  const from = params.get("from") || def.from;
  const to = params.get("to") || def.to;
  const tab: Tab = TABS.some((t) => t.id === params.get("tab"))
    ? (params.get("tab") as Tab)
    : "unanswered";
  const set = (next: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) out.set(k, v);
    if (out.get("tab") === "unanswered") out.delete("tab");
    setParams(out, { replace: true });
  };
  const report = useQuery({
    queryKey: ["complaints", from, to],
    queryFn: () => loadComplaints(from, to),
  });

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div
        className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border"
        data-annot="шапка жалоб"
      >
        <h1 className="pb-2 text-[16px] font-semibold tracking-[-0.015em]">
          Жалобы
        </h1>
        <div role="tablist" aria-label="Разделы жалоб" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => set({ tab: t.id })}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                tab === t.id
                  ? "border-accent font-medium text-fg"
                  : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
              {t.id === "unanswered" && report.data?.total.unanswered ? (
                <span className="ml-1.5 rounded bg-err/15 px-1.5 text-[11px] text-err">
                  {report.data.total.unanswered}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
      <div
        className="flex flex-wrap items-center gap-1.5 text-[12.5px]"
        data-annot="выбор периода"
      >
        <button
          type="button"
          className={btn}
          onClick={() => set(shiftPeriod(from, to, -1))}
          aria-label="предыдущий период"
        >
          <ChevronLeft size={14} />
        </button>
        <input
          type="date"
          value={from}
          onChange={(e) => e.target.value && set({ from: e.target.value })}
          className={input}
        />
        <span className="text-fg-faint">—</span>
        <input
          type="date"
          value={to}
          onChange={(e) => e.target.value && set({ to: e.target.value })}
          className={input}
        />
        <button
          type="button"
          className={btn}
          onClick={() => set(shiftPeriod(from, to, 1))}
          aria-label="следующий период"
        >
          <ChevronRight size={14} />
        </button>
        {presets().map((p) => (
          <button
            key={p.id}
            type="button"
            className={`${btn} ${p.from === from && p.to === to ? "border-accent text-fg" : ""}`}
            onClick={() => set({ from: p.from, to: p.to })}
          >
            {p.label}
          </button>
        ))}
      </div>
      {report.isPending ? (
        <div className="text-[13px] text-fg-muted">Загружаю жалобы из CRM…</div>
      ) : report.isError ? (
        <div className="text-[13px] text-err">
          Не загрузилось: {String(report.error)}
        </div>
      ) : (
        <>
          <Tiles r={report.data} />
          {tab === "unanswered" && <Unanswered r={report.data} />}
          {tab === "salons" && <Salons r={report.data} />}
          {tab === "list" && <List r={report.data} />}
        </>
      )}
    </main>
  );
}
